import {
  getAllReports,
  updateReport,
  getReportLogs,
} from "modules/finops/conciliaciones/services/conciliacionService";
import { useState, useCallback, useMemo, useRef } from "react";
import { commerceName, groupLogCounts, matchesCommerce, readMetadata } from "../model/reportLogs";

export const useReports = (reportId = 1, reportName = "RejectedStep") => {
  // Estados existentes
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showEmailsModal, setShowEmailsModal] = useState(false);

  // Estados para logs
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedCommerceLogs, setSelectedCommerceLogs] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);

  // Estado para conteo de logs por comercio
  const [logsCountByCommerce, setLogsCountByCommerce] = useState({});
  const [unassignedLogsCount, setUnassignedLogsCount] = useState(0);
  const logsCache = useRef(null);
  const logsRequest = useRef(0);

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
    details: "",
  });

  // Constantes

  // ========== FUNCIÓN PARA NORMALIZAR NOMBRE DE COMERCIO ==========
  const normalizeCommerceName = useCallback((commerceString) => {
    if (!commerceString) return "";

    // Caso 1: "ACCENORTE (10101) (10101)" -> "ACCENORTE"
    // Caso 2: "ACCENORTE (10101)" -> "ACCENORTE"
    // Caso 3: "ACCENORTE" -> "ACCENORTE"

    // Eliminar todo lo que está entre paréntesis (incluyendo paréntesis repetidos)
    // Esto maneja tanto " (10101)" como " (10101) (10101)"
    let normalized = commerceString.replace(/\s*\([^)]*\)\s*/g, "").trim();

    // Si después de limpiar queda vacío, usar el original
    if (!normalized) {
      normalized = commerceString.trim();
    }

    return normalized;
  }, []);

  // ========== NOTIFICACIONES ==========
  const showNotification = useCallback((message, severity = "info", details = "") => {
    setNotification({
      open: true,
      message,
      severity,
      details,
    });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // ========== OBTENER CONTEO DE LOGS POR COMERCIO ==========
  const fetchLogsCount = useCallback(async () => {
    try {
      const result = await getReportLogs({ id: reportId, name: reportName });
      if (!result.success || !Array.isArray(result.data))
        throw new Error(result.error || "No se pudo cargar el registro de correos");
      logsCache.current = result.data;
      setUnassignedLogsCount(
        result.data.filter((log) => !readMetadata(log.metadata).commerce).length
      );
      setLogsCountByCommerce(groupLogCounts(result.data));
    } catch (err) {
      logsCache.current = null;
      setUnassignedLogsCount(0);
      setLogsCountByCommerce({});
      showNotification(err.message || "No se pudo cargar el registro de correos", "error");
    }
  }, [showNotification, reportId, reportName]);

  // ========== FETCH DE REPORTES ==========
  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAllReports();

      const reportsData = response.data && Array.isArray(response.data) ? response.data.flat() : [];

      const configuredReport = reportsData.filter(
        (report) => String(report.id) === String(reportId)
      );

      setReports(configuredReport);
      await fetchLogsCount();

      if (configuredReport.length > 0) {
        const configCount = configuredReport[0]?.config?.params?.length || 0;
        showNotification(
          `Reporte ${reportName} cargado con ${configCount} configuraciones`,
          "success"
        );
      }

      return configuredReport;
    } catch (err) {
      console.error("Error al obtener reportes:", err);
      const errorMessage = err.message || "Error al cargar los reportes";
      const errorDetails = err.response?.data?.details || err.response?.data?.message || "";
      setError(errorMessage);
      showNotification(errorMessage, "error", errorDetails);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [showNotification, fetchLogsCount, reportId, reportName]);

  // ========== OBTENER LOGS DE UN COMERCIO ESPECÍFICO ==========
  const fetchCommerceLogs = useCallback(
    async (comercioNombre, comercioId, forceRefresh = false) => {
      const request = ++logsRequest.current;
      setLogsLoading(true);
      setLogsError(null);
      setLogs([]);
      try {
        let data = logsCache.current;
        if (!data || forceRefresh) {
          const result = await getReportLogs({ id: reportId, name: reportName });
          if (!result.success || !Array.isArray(result.data))
            throw new Error(result.error || "Error al cargar el registro de correos");
          data = result.data;
          if (request !== logsRequest.current) return;
          logsCache.current = data;
          setUnassignedLogsCount(data.filter((log) => !readMetadata(log.metadata).commerce).length);
          setLogsCountByCommerce(groupLogCounts(data));
        }
        if (request === logsRequest.current)
          setLogs(data.filter((log) => matchesCommerce(log, comercioNombre, comercioId)));
      } catch (err) {
        if (request === logsRequest.current)
          setLogsError(err.message || "Error al cargar el registro de correos");
      } finally {
        if (request === logsRequest.current) setLogsLoading(false);
      }
    },
    [reportId, reportName]
  );

  // ========== VER LOGS DE COMERCIO ==========
  const viewCommerceLogs = useCallback(
    (report) => {
      setSelectedCommerceLogs(report);
      fetchCommerceLogs(report.comercio, report.comercioId);
      setShowLogsModal(true);
    },
    [fetchCommerceLogs]
  );

  // ========== CERRAR MODAL DE LOGS ==========
  const closeLogsModal = useCallback(() => {
    logsRequest.current++;
    setLogsLoading(false);
    setShowLogsModal(false);
    setSelectedCommerceLogs(null);
    setLogs([]);
    setLogsError(null);
  }, []);

  // ========== ACTUALIZAR REPORTE LOCAL ==========
  const updateLocalReport = useCallback((updatedReport) => {
    setReports((prevReports) => {
      return prevReports.map((report) => {
        if (report.id === updatedReport.id || report.name === updatedReport.name) {
          return updatedReport;
        }
        return report;
      });
    });
  }, []);

  // ========== ACTUALIZAR CORREO ==========
  const updateEmail = useCallback(
    async (originalReportId, paramIndex, emailIndex, newEmail) => {
      try {
        setLoading(true);

        const originalReport = reports.find(
          (report) =>
            String(report.id) === String(reportId) && String(report.id) === String(originalReportId)
        );

        if (!originalReport) {
          throw new Error(`Reporte ${reportName} no encontrado`);
        }

        const updatedReport = JSON.parse(JSON.stringify(originalReport));
        updatedReport.config.params[paramIndex].emails[emailIndex].Email = newEmail;

        const updateBody = {
          id: updatedReport.id,
          config: updatedReport.config,
        };

        const response = await updateReport(updateBody);
        updateLocalReport(updatedReport);

        showNotification("Correo actualizado exitosamente", "success");

        return response;
      } catch (err) {
        console.error("Error al actualizar correo:", err);
        showNotification(err.message, "error");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [reports, showNotification, updateLocalReport, reportId, reportName]
  );

  // ========== AGREGAR CORREO ==========
  const addNewEmail = useCallback(
    async (originalReportId, paramIndex, newEmailData) => {
      try {
        setLoading(true);

        const originalReport = reports.find(
          (report) =>
            String(report.id) === String(reportId) && String(report.id) === String(originalReportId)
        );

        if (!originalReport) {
          throw new Error(`Reporte ${reportName} no encontrado`);
        }

        const updatedReport = JSON.parse(JSON.stringify(originalReport));
        updatedReport.config.params[paramIndex].emails.push(newEmailData);

        const updateBody = {
          id: updatedReport.id,
          config: updatedReport.config,
        };

        const response = await updateReport(updateBody);
        updateLocalReport(updatedReport);

        showNotification("Correo agregado exitosamente", "success");

        return response;
      } catch (err) {
        console.error("Error al agregar correo:", err);
        showNotification(err.message, "error");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [reports, showNotification, updateLocalReport, reportId, reportName]
  );

  // ========== ELIMINAR CORREO ==========
  const deleteEmail = useCallback(
    async (originalReportId, paramIndex, emailIndex) => {
      try {
        setLoading(true);

        const originalReport = reports.find(
          (report) =>
            String(report.id) === String(reportId) && String(report.id) === String(originalReportId)
        );

        if (!originalReport) {
          throw new Error(`Reporte ${reportName} no encontrado`);
        }

        const emailsCount = originalReport.config.params[paramIndex].emails.length;

        if (emailsCount <= 1) {
          const updatedReport = JSON.parse(JSON.stringify(originalReport));
          updatedReport.config.params[paramIndex].emails[emailIndex].Email = "";

          const updateBody = {
            id: updatedReport.id,
            config: updatedReport.config,
          };

          await updateReport(updateBody);
          updateLocalReport(updatedReport);

          showNotification("Correo vaciado exitosamente", "info");
          return;
        }

        const updatedReport = JSON.parse(JSON.stringify(originalReport));
        updatedReport.config.params[paramIndex].emails.splice(emailIndex, 1);

        const updateBody = {
          id: updatedReport.id,
          config: updatedReport.config,
        };

        const response = await updateReport(updateBody);
        updateLocalReport(updatedReport);

        showNotification("Correo eliminado exitosamente", "success");
        return response;
      } catch (err) {
        console.error("Error al eliminar correo:", err);
        showNotification(err.message, "error");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [reports, showNotification, updateLocalReport, reportId, reportName]
  );

  // ========== GESTIÓN DE MODALES ==========
  const viewEmails = useCallback((report) => {
    setSelectedReport(report);
    setShowEmailsModal(true);
  }, []);

  const closeEmailsModal = useCallback(() => {
    setShowEmailsModal(false);
    setSelectedReport(null);
  }, []);

  // ========== PROCESAMIENTO DE DATOS ==========
  const processParamData = (param, index, configuredReport) => {
    if (!param || !param.emails || !Array.isArray(param.emails)) {
      return null;
    }

    const firstEmail = param.emails[0] || {};
    const validEmails = param.emails.filter(
      (email) => email.Email && email.Email.trim() !== ""
    ).length;

    let comercioNombre =
      firstEmail.Name && firstEmail.Name !== "Sin nombre"
        ? firstEmail.Name
        : param.value
        ? `Comercio ${param.value}`
        : "Sin nombre";

    // Limpiar el nombre si viene con el formato "NOMBRE (ID)"
    if (comercioNombre.includes("(")) {
      comercioNombre = comercioNombre.split("(")[0].trim();
    }

    const commerceFullString = firstEmail.Name || `${comercioNombre} (${param.value || "N/A"})`;
    const normalizedCommerceName = normalizeCommerceName(commerceFullString);

    const logData = logsCountByCommerce[`id:${param.value}`] ||
      logsCountByCommerce[`name:${commerceName(commerceFullString)}`] || {
        logs: [],
        totalLogs: 0,
        totalMessages: 0,
        lastLogDate: null,
      };

    return {
      id: `${configuredReport.id}-${index + 1}`,
      originalReportId: configuredReport.id,
      paramIndex: index,
      comercioId: param.value || "N/A",
      comercio: comercioNombre,
      comercioCompleto: commerceFullString,
      comercioNormalizado: normalizedCommerceName,
      reporte: configuredReport.name,
      totalCorreos: param.emails.length,
      correosValidos: validEmails,
      estado: configuredReport.status ? "Activo" : "Inactivo",
      fechaCreacion: configuredReport.createdAt
        ? new Date(configuredReport.createdAt).toLocaleDateString("es-ES")
        : "N/A",
      config: configuredReport.config,
      paramData: param,
      totalLogs: logData.totalLogs,
      logsAvailable: logsCache.current !== null,
      totalMessages: logData.totalMessages,
      lastLogDate: logData.lastLogDate,
    };
  };

  const tableData = useMemo(() => {
    if (!reports || !Array.isArray(reports) || reports.length === 0) {
      return [];
    }

    const configuredReport = reports.find((report) => String(report.id) === String(reportId));

    if (!configuredReport?.config?.params) {
      return [];
    }

    return configuredReport.config.params
      .map((param, index) => processParamData(param, index, configuredReport))
      .filter((item) => item !== null);
  }, [reports, logsCountByCommerce, normalizeCommerceName, reportId]);

  // ========== ESTADÍSTICAS ==========
  const stats = useMemo(() => {
    const totalConfigurations = tableData.length;
    const activeConfigurations = tableData.filter((r) => r.estado === "Activo").length;
    const totalEmails = tableData.reduce((sum, item) => sum + item.totalCorreos, 0);
    const totalValidEmails = tableData.reduce((sum, item) => sum + item.correosValidos, 0);
    const invalidEmails = totalEmails - totalValidEmails;
    const totalLogs = tableData.reduce((sum, item) => sum + (item.totalLogs || 0), 0);
    const totalMessages = tableData.reduce((sum, item) => sum + (item.totalMessages || 0), 0);

    return {
      totalReports: totalConfigurations,
      activeReports: activeConfigurations,
      inactiveReports: totalConfigurations - activeConfigurations,
      totalEmails: totalEmails,
      totalValidEmails: totalValidEmails,
      emailsWithoutEmail: invalidEmails,
      validPercentage: totalEmails > 0 ? Math.round((totalValidEmails / totalEmails) * 100) : 0,
      totalLogs: totalLogs,
      totalMessages: totalMessages,
    };
  }, [tableData]);

  // ========== REFRESCAR DATOS ==========
  const refreshData = useCallback(async () => {
    try {
      await fetchReports();
      showNotification("Datos actualizados correctamente", "success");
    } catch (err) {
      showNotification("Error al actualizar datos", "error", err.message);
    }
  }, [fetchReports, showNotification]);

  // ========== RETORNO DEL HOOK ==========
  return {
    unassignedLogsCount,
    reports: tableData,
    loading,
    error,
    selectedReport,
    showEmailsModal,
    notification,
    stats,
    showLogsModal,
    selectedCommerceLogs,
    logs,
    logsLoading,
    logsError,
    showNotification,
    closeNotification,
    fetchReports,
    refreshData,
    updateEmail,
    addNewEmail,
    deleteEmail,
    viewEmails,
    closeEmailsModal,
    viewCommerceLogs,
    closeLogsModal,
    fetchCommerceLogs,
  };
};
